'use client';

import Link from 'next/link';
import { FileText, ArrowRight, ChevronRight, Inbox } from 'lucide-react';
import { useQuery } from '@apollo/client/react';
import {
  RECENT_DOCUMENTS_QUERY,
  type Document,
  DocumentType,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import {
  useAdmin,
  useSessionNeedsRenewal,
  SessionRenewalNotice,
} from '@paeffgen/react/crm-documents/feature-auth';
import { Skeleton, EmptyState } from '@paeffgen/react/crm-documents/ui';
import {
  formatCurrency,
  formatDate,
  TYPE_BADGES,
  TYPE_LABELS,
} from '@paeffgen/react/crm-documents/util';

function RecentActivitySkeleton() {
  return (
    <div className="divide-y divide-zinc-100">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-5">
          <Skeleton className="w-10 h-10 rounded-lg shrink-0" />
          <div className="min-w-0 flex-1 sm:grid sm:grid-cols-3 sm:gap-4 items-center">
            <div>
              <Skeleton className="h-4 w-32 mb-2" />
              <Skeleton className="h-3 w-20" />
            </div>
            <div className="hidden sm:block">
              <Skeleton className="h-4 w-48" />
            </div>
            <div className="flex justify-end pr-8">
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function RecentActivity() {
  const { adminQueryVars } = useAdmin();
  const needsRenewal = useSessionNeedsRenewal();

  const { data, loading, error } = useQuery<{
    recentDocuments: Document[];
  }>(RECENT_DOCUMENTS_QUERY, {
    variables: { limit: 5, ...adminQueryVars },
    fetchPolicy: 'cache-and-network',
  });

  const recentDocs = data?.recentDocuments ?? [];

  const renderContent = () => {
    if (loading && !data) {
      return <RecentActivitySkeleton />;
    }

    if (error) {
      // Kein Button: die Aufforderung steht schon oben in der Status-Übersicht,
      // die bei einer erneuerungsbedürftigen Session immer mitbetroffen ist.
      return needsRenewal ? (
        <SessionRenewalNotice variant="plain" />
      ) : (
        <div className="p-8 text-center text-red-600">
          Fehler beim Laden der letzten Belege.
        </div>
      );
    }

    if (recentDocs.length === 0) {
      return (
        <div className="p-8">
          <EmptyState
            icon={Inbox}
            title="Keine Belege"
            description="Es wurden noch keine Belege oder Aufträge erstellt."
            action={{ label: 'Zu den Belegen', href: '/belege' }}
          />
        </div>
      );
    }

    return (
      <div className="divide-y divide-zinc-100">
        {recentDocs.map((doc) => (
          <Link
            key={doc.id}
            href={`/belege?open=${doc.id}`}
            className="flex items-center gap-4 p-5 hover:bg-zinc-50 transition-colors group"
          >
            <div
              className={`p-2.5 rounded-lg ${TYPE_BADGES[doc.type]} shrink-0 shadow-sm`}
            >
              <FileText size={20} strokeWidth={1.5} />
            </div>

            <div className="min-w-0 flex-1 sm:grid sm:grid-cols-3 sm:gap-4 items-center">
              <div>
                <p className="text-sm font-bold text-secondary truncate">
                  {TYPE_LABELS[doc.type]} {doc.documentNumber}
                </p>
                <p className="text-xs text-zinc-500 font-medium mt-0.5">
                  {formatDate(doc.documentDate)}
                </p>
              </div>

              <div className="hidden sm:block">
                <p
                  className="text-sm text-zinc-600 truncate"
                  title={doc.commission || '-'}
                >
                  {doc.commission || (
                    <span className="text-zinc-400 italic">
                      Keine Kommission
                    </span>
                  )}
                </p>
              </div>

              <div className="flex items-center justify-end gap-4">
                {doc.type !== DocumentType.LIEFERSCHEIN && (
                  <span className="text-sm font-bold tabular-nums text-secondary tracking-tight">
                    {doc.netTotal != null ? formatCurrency(doc.netTotal) : '-'}
                  </span>
                )}
                <ChevronRight
                  size={18}
                  className="text-zinc-300 group-hover:text-primary transition-colors"
                />
              </div>
            </div>
          </Link>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-[0_2px_4px_rgba(44,44,43,0.05)] border border-zinc-200 overflow-hidden">
      <div className="p-6 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
        <h2 className="text-lg font-bold text-secondary tracking-tight">
          Letzte Belege
        </h2>
        <Link
          href="/belege"
          className="text-sm font-bold text-primary hover:text-primary-hover flex items-center gap-1 transition-colors"
        >
          Alle anzeigen <ArrowRight size={14} />
        </Link>
      </div>

      {renderContent()}
    </div>
  );
}
